import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { PencilIcon, UserAddIcon, OfficeBuildingIcon, DocumentAddIcon, ClockIcon } from '@heroicons/react/outline';
import BranchFormModal from '../components/BranchFormModal';
import UserFormModalRBAC from '../components/UserFormModalRBAC';
import PolicyFormModal from '../components/PolicyFormModal';
import MandatoryCarInsuranceModal from '../components/MandatoryCarInsuranceModal';
import CargoInsuranceModal from '../components/CargoInsuranceModal';
import { useLanguage } from '../contexts/LanguageContext';

export default function BranchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [branch, setBranch] = useState(null);
  const [branchUsers, setBranchUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);
  const [selectedPolicyType, setSelectedPolicyType] = useState(null);
  const { t } = useLanguage();

  useEffect(() => {
    // Check if id is valid
    if (!id || id === 'undefined') {
      toast.error(t('branchDetail.invalidBranchId') || 'Invalid branch ID. Please log out and log back in.');
      navigate('/dashboard');
      return;
    }
    fetchBranchDetails();
    fetchCompanies();
  }, [id]);

  const fetchBranchDetails = async () => {
    try {
      setIsLoading(true);
      
      // Fetch branch details
      const branchResponse = await api.get(`/api/branches/${id}`);
      setBranch(branchResponse.data);
      
      // Fetch branch users
      const usersResponse = await api.get(`/api/users/branch/${id}`);
      setBranchUsers(usersResponse.data);
      
    } catch (error) {
      console.error('Error fetching branch details:', error);
      toast.error(t('branches.fetchError'));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      // Only admin would need this for the edit modal
      const response = await api.get('/api/companies');
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error(t('companies.fetchError'));
    }
  };

  const handleEditBranch = async (values) => {
    try {
      await api.put(`/api/branches/${id}`, values);
      toast.success(t('branches.branchUpdated'));
      setIsEditModalOpen(false);
      fetchBranchDetails();
    } catch (error) {
      console.error('Error updating branch:', error);
      toast.error(error.response?.data?.message || t('branches.branchUpdateFailed'));
    }
  };

  const handleAddUser = async (values) => {
    try {
      // Make sure it's a branch user
      values.role = 'branch';
      values.branchId = parseInt(id);
      values.companyId = branch.companyId;
      
      // Ensure password meets minimum length
      if (values.password && values.password.length < 6) {
        toast.error(t('forms.passwordRequirement'));
        return;
      }
      
      await api.post('/api/users', values);
      toast.success(t('users.userAdded'));
      setIsAddUserModalOpen(false);
      fetchBranchDetails();
    } catch (error) {
      console.error('Error adding user:', error);
      toast.error(error.response?.data?.message || t('users.userAddFailed'));
    }
  };

  const handlePolicyTypeClick = (policyType) => {
    if (policyType === 'coming-soon') {
      toast.info(t('branchDetail.comingSoon'));
      return;
    }
    setSelectedPolicyType(policyType);
    setIsPolicyModalOpen(true);
  };

  const handleAddPolicy = async (policyData) => {
    try {
      // If policy already has an ID, it was created via draft/activate flow
      // No need to POST again, just close the modal
      if (policyData.id) {
        toast.success(t('branchDetail.policyAdded'));
        setIsPolicyModalOpen(false);
        setSelectedPolicyType(null);
        return;
      }
      
      // Otherwise, create the policy (for older modals that don't use draft flow)
      // Add branch and company info
      policyData.branchId = parseInt(id);
      policyData.companyId = branch.companyId;
      
      await api.post('/api/policies', policyData);
      toast.success(t('branchDetail.policyAdded'));
      setIsPolicyModalOpen(false);
      setSelectedPolicyType(null);
    } catch (error) {
      console.error('Error adding policy:', error);
      toast.error(error.response?.data?.message || t('branchDetail.policyAddFailed'));
    }
  };

  if (isLoading) {
    return <div className="py-6 px-4 sm:px-6 lg:px-8">{t('common.loading')}</div>;
  }

  if (!branch) {
    return <div className="py-6 px-4 sm:px-6 lg:px-8">{t('common.noData')}</div>;
  }

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{branch.name}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('branchDetail.branchId')}: {branch.id}</p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-4 flex">
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="btn-secondary mr-2 flex items-center"
          >
            <PencilIcon className="mr-2 h-5 w-5" />
            {t('branchDetail.editBranch')}
          </button>
          <button
            onClick={() => setIsAddUserModalOpen(true)}
            className="btn-primary flex items-center"
          >
            <UserAddIcon className="mr-2 h-5 w-5" />
            {t('branchDetail.addUser')}
          </button>
        </div>
      </div>

      {/* Policy Type Selection Cards */}
      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {/* Mandatory Car Insurance */}
        <div
          onClick={() => handlePolicyTypeClick('Mandatory Car Insurance')}
          className="bg-white overflow-hidden shadow rounded-lg hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-blue-500"
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentAddIcon className="h-8 w-8 text-blue-600" aria-hidden="true" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t('branchDetail.mandatoryCarInsurance')}
                  </dt>
                  <dd className="mt-1 text-xs text-gray-400">
                    {t('branchDetail.mandatoryCarInsuranceDesc')}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 px-5 py-3">
            <div className="text-sm">
              <span className="font-medium text-blue-700 hover:text-blue-900">
                {t('branchDetail.createPolicy')} →
              </span>
            </div>
          </div>
        </div>

        {/* 3rd Party (Car TPL) */}
        <div
          onClick={() => handlePolicyTypeClick('Car TPL')}
          className="bg-white overflow-hidden shadow rounded-lg hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-green-500"
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentAddIcon className="h-8 w-8 text-green-600" aria-hidden="true" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t('branchDetail.thirdPartyInsurance')}
                  </dt>
                  <dd className="mt-1 text-xs text-gray-400">
                    {t('branchDetail.thirdPartyInsuranceDesc')}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-green-50 px-5 py-3">
            <div className="text-sm">
              <span className="font-medium text-green-700 hover:text-green-900">
                {t('branchDetail.createPolicy')} →
              </span>
            </div>
          </div>
        </div>

        {/* Cargo Transport Insurance */}
        <div
          onClick={() => handlePolicyTypeClick('Cargo Transport Insurance')}
          className="bg-white overflow-hidden shadow rounded-lg hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-yellow-500"
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentAddIcon className="h-8 w-8 text-yellow-600" aria-hidden="true" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t('branchDetail.cargoInsurance')}
                  </dt>
                  <dd className="mt-1 text-xs text-gray-400">
                    {t('branchDetail.cargoInsuranceDesc')}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-yellow-50 px-5 py-3">
            <div className="text-sm">
              <span className="font-medium text-yellow-700 hover:text-yellow-900">
                {t('branchDetail.createPolicy')} →
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">{t('branchDetail.branchInformation')}</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">{t('branchDetail.branchInfoDescription', { name: branch.name })}</p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
          <dl className="sm:divide-y sm:divide-gray-200">
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">{t('branchDetail.name')}</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{branch.name}</dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">{t('branchDetail.email')}</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{branch.email}</dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">{t('branchDetail.company')}</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {branch.company ? (
                  <Link 
                    to={`/companies/${branch.company.id}`}
                    className="text-indigo-600 hover:text-indigo-900"
                  >
                    {branch.company.name}
                  </Link>
                ) : 'N/A'}
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">{t('branchDetail.phone')}</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{branch.phone || 'N/A'}</dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">{t('branchDetail.address')}</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{branch.address || 'N/A'}</dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">{t('branchDetail.status')}</dt>
              <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
                <span
                  className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                    branch.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}
                >
                  {branch.isActive ? t('branches.active') : t('branches.inactive')}
                </span>
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Branch Users Section */}
      <div className="mt-8">
        <div className="sm:flex sm:items-center sm:justify-between">
          <h2 className="text-xl font-semibold text-gray-900">{t('branchDetail.branchUsers')}</h2>
        </div>
        
        {branchUsers.length === 0 ? (
          <div className="mt-4 bg-white shadow overflow-hidden sm:rounded-lg p-6 text-center text-gray-500">
            {t('branchDetail.noUsers')}
          </div>
        ) : (
          <div className="mt-4 flex flex-col">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('branchDetail.username')}</th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('branchDetail.email')}</th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('branchDetail.status')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {branchUsers.map((user) => (
                      <tr key={user.id}>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{user.username}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{user.email}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <span
                            className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                              user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {user.isActive ? t('users.active') : t('users.inactive')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Branch Modal */}
      <BranchFormModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleEditBranch}
        initialValues={branch}
        companies={companies}
        userRole="admin" // Set to admin to allow company selection in edit mode
      />

      {/* Add User Modal */}
      <UserFormModalRBAC
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        onSubmit={handleAddUser}
      />

      {/* Policy Modals */}
      {selectedPolicyType === 'Mandatory Car Insurance' && (
        <MandatoryCarInsuranceModal
          isOpen={isPolicyModalOpen}
          onClose={() => {
            setIsPolicyModalOpen(false);
            setSelectedPolicyType(null);
          }}
          onSubmit={handleAddPolicy}
          initialValues={null}
        />
      )}

      {selectedPolicyType === 'Car TPL' && (
        <PolicyFormModal
          isOpen={isPolicyModalOpen}
          onClose={() => {
            setIsPolicyModalOpen(false);
            setSelectedPolicyType(null);
          }}
          onSubmit={handleAddPolicy}
          initialValues={null}
        />
      )}

      {selectedPolicyType === 'Cargo Transport Insurance' && (
        <CargoInsuranceModal
          isOpen={isPolicyModalOpen}
          onClose={() => {
            setIsPolicyModalOpen(false);
            setSelectedPolicyType(null);
          }}
          onSubmit={handleAddPolicy}
          initialValues={null}
          branch={branch}
        />
      )}
    </div>
  );
} 